/**
 * Employee Service
 * API calls for employee management
 */

import apiClient from '../client';
import type { Employee, PaginatedResponse } from '../../types';

interface EmployeeFilters {
  page?: number;
  size?: number;
}

const employeeService = {
  /**
   * Get all employees with pagination
   */
  getEmployees: async (filters: EmployeeFilters = {}) => {
    const { page = 0, size = 20 } = filters;
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await apiClient.get<PaginatedResponse<Employee>>(
      `/employees?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get employees eligible for salary processing
   */
  getEmployeesEligibleForSalary: async () => {
    const response = await apiClient.get<Employee[]>('/employees/eligible-for-salary');
    return response.data;
  },

  /**
   * Get employee by ID
   */
  getEmployeeById: async (id: string) => {
    const response = await apiClient.get<Employee>(`/employees/${id}`);
    return response.data;
  },

  /**
   * Create new employee
   */
  createEmployee: async (data: Partial<Employee>) => {
    const response = await apiClient.post<Employee>('/employees', data);
    return response.data;
  },

  /**
   * Update employee
   */
  updateEmployee: async (id: string, data: Partial<Employee>) => {
    const response = await apiClient.put<Employee>(`/employees/${id}`, data);
    return response.data;
  },

  activateEmployee: async (id: string) => {
    await apiClient.patch(`/employees/${id}/activate`);
  },

  /**
   * Delete employee
   */
  deleteEmployee: async (id: string) => {
    await apiClient.delete(`/employees/${id}`);
  },

  search: async (params: { page?: number; size?: number; search?: string; active?: boolean } = {}) => {
    const { page = 0, size = 10, search, active } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (active !== undefined) qs.set('active', String(active));
    const response = await apiClient.get<PaginatedResponse<Employee>>(`/employees/search?${qs}`);
    return response.data;
  },
};

export default employeeService;
